import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import NovelOutlinePage from './NovelOutlinePage'
import NovelResultPage from './NovelResultPage'
import { generateNovelOutline, countWords } from './novelUtils'
import { MODEL_MODES } from "../../constants/agentConfig"
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { AGENT_TYPES } from '../../constants/agentConfig.js'
import AgentSubscriptionWrapper from '../../components/AgentSubscriptionWrapper'
import { useAuth } from '../../hooks/useSupabaseAuth'

// Novel Agent States
const AGENT_STATES = {
  OUTLINE: 'outline',
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const NovelAgentContent = ({ query, onBack, llmService }) => {
  const [currentState, setCurrentState] = useState(AGENT_STATES.OUTLINE)
  const [outline, setOutline] = useState([])
  const [generatedContent, setGeneratedContent] = useState({})
  const [progress, setProgress] = useState({ current: 0, total: 0 })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasInitialized, setHasInitialized] = useState(false)
  const [novelConfig, setNovelConfig] = useState({
    genre: 'Fiksi',
    writingStyle: 'Naratif',
    plotStructure: 'Three-Act Structure',
    language: 'Bahasa Indonesia'
  })
  const [currentSection, setCurrentSection] = useState(null)
  const [startTime, setStartTime] = useState(null)

  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  const { user } = useAuth()

  // Use refs to track state and prevent unnecessary re-generation
  const isGeneratingOutline = useRef(false)
  const currentQuery = useRef(query)
  const hasEverGenerated = useRef(false)
  const generationController = useRef(null)
  const isPageVisible = useRef(true)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `novel_agent_${query}_${suffix}`

  // Load persisted state on component mount
  useEffect(() => {
    if (!hasInitialized && query && query.trim()) {
      try {
        // Load persisted state
        const savedState = sessionStorage.getItem(getSessionKey('state'))
        const savedOutline = sessionStorage.getItem(getSessionKey('outline'))
        const savedContent = sessionStorage.getItem(getSessionKey('content'))
        const savedProgress = sessionStorage.getItem(getSessionKey('progress'))
        const savedConfig = sessionStorage.getItem(getSessionKey('config'))
        const savedStartTime = sessionStorage.getItem(getSessionKey('startTime'))
        const savedIsLoading = sessionStorage.getItem(getSessionKey('isLoading'))

        if (savedState && savedOutline) {
          setCurrentState(savedState)
          setOutline(JSON.parse(savedOutline))
          
          if (savedContent) {
            setGeneratedContent(JSON.parse(savedContent))
          }
          
          if (savedProgress) {
            setProgress(JSON.parse(savedProgress))
          }
          
          if (savedConfig) {
            setNovelConfig(JSON.parse(savedConfig))
          }
          
          if (savedStartTime) {
            setStartTime(parseInt(savedStartTime))
          }
          
          const wasLoading = savedIsLoading === 'true'
          
          // If was generating and loading, auto-resume generation
          if (savedState === AGENT_STATES.GENERATING && wasLoading) {
            console.log('📂 Restored active generation state - auto-resuming')
            // Auto-resume generation without credit consumption
            setTimeout(() => {
              handleContinueGenerationWithoutCredits()
            }, 1000)
          }
          
          console.log('📂 Loaded saved novel state from session storage', {
            state: savedState,
            loading: wasLoading
          })
        }
      } catch (error) {
        console.error('Error loading persisted state:', error)
        clearSessionStorage()
      }
      
      setHasInitialized(true)
    }
  }, [query, hasInitialized])

  // Page Visibility API - only for tracking, no interruption of generation
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden
      isPageVisible.current = isVisible
      
      if (isVisible) {
        console.log('📱 User returned to page')
      } else {
        console.log('📱 User left page - generation continues in background')
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  // Save state to session storage
  const saveState = (state, outlineData, contentData, progressData, loadingState = isLoading) => {
    try {
      sessionStorage.setItem(getSessionKey('state'), state)
      if (outlineData) sessionStorage.setItem(getSessionKey('outline'), JSON.stringify(outlineData))
      if (contentData) sessionStorage.setItem(getSessionKey('content'), JSON.stringify(contentData))
      if (progressData) sessionStorage.setItem(getSessionKey('progress'), JSON.stringify(progressData))
      sessionStorage.setItem(getSessionKey('isLoading'), loadingState.toString())
      if (startTime) {
        sessionStorage.setItem(getSessionKey('startTime'), startTime.toString())
      }
      if (novelConfig) {
        sessionStorage.setItem(getSessionKey('config'), JSON.stringify(novelConfig))
      }
    } catch (error) {
      console.error('Error saving state:', error)
    }
  }

  // Clear session storage
  const clearSessionStorage = () => {
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('outline'))
      sessionStorage.removeItem(getSessionKey('content'))
      sessionStorage.removeItem(getSessionKey('progress'))
      sessionStorage.removeItem(getSessionKey('config'))

      sessionStorage.removeItem(getSessionKey('isLoading'))
      sessionStorage.removeItem(getSessionKey('startTime'))
    } catch (error) {
      console.error('Error clearing session storage:', error)
    }
  }

  // Save state whenever key values change
  useEffect(() => {
    if (hasInitialized && currentState !== AGENT_STATES.OUTLINE) {
      saveState(currentState, outline, generatedContent, progress, isLoading)
    }
  }, [hasInitialized, currentState, generatedContent, progress, isLoading])

  // Handle outline generation
  const handleGenerateOutline = async (novelConfig) => {
    if (isGeneratingOutline.current) return null
    
    isGeneratingOutline.current = true
    setIsLoading(true)
    setError(null)

    try {
      console.log('🎭 Generating novel outline with config:', novelConfig)
      const outlineData = await generateNovelOutline(query, llmService, novelConfig)
      
      if (outlineData && outlineData.length > 0) {
        setOutline(outlineData)
        saveState(AGENT_STATES.OUTLINE, outlineData, null, null)
        console.log('✅ Novel outline generated successfully')
        return outlineData
      } else {
        throw new Error('Gagal menghasilkan outline novel')
      }
    } catch (error) {
      console.error('❌ Error generating novel outline:', error)
      setError(error.message || 'Terjadi kesalahan saat menghasilkan outline novel')
      return null
    } finally {
      setIsLoading(false)
      isGeneratingOutline.current = false
    }
  }

  // Handle novel generation
  const handleStartGeneration = async (finalOutline, novelConfig) => {
    // Check if this is a completely new generation (fresh start)
    // This happens when user clicks "Mulai Generasi" from outline page
    const isCompletelyNewGeneration = currentState === AGENT_STATES.OUTLINE
    
    // Reset generated content for completely new generation
    let newGeneratedContent
    if (isCompletelyNewGeneration) {
      newGeneratedContent = {}
      setGeneratedContent({})
      // Clear any existing session storage for this query to prevent conflicts
      clearSessionStorage()
      console.log('🔄 Resetting generated content and session storage for new generation')
    } else {
      newGeneratedContent = { ...generatedContent }
    }
    
    const startIndex = Object.keys(newGeneratedContent).length
    
    // Only consume credits if starting from the very beginning (no existing content)
    const isNewGeneration = startIndex === 0 && Object.keys(newGeneratedContent).length === 0
    
    if (isNewGeneration) {
      const canProceed = await checkAndWarnCredits(AGENT_TYPES.NOVEL)
      if (!canProceed) return
    }

    setCurrentState(AGENT_STATES.GENERATING)
    setIsLoading(true)
    setError(null)

    
    // Only set start time if this is a fresh start
    if (!startTime || isNewGeneration) {
      setStartTime(Date.now())
    }
    
    // Update progress to reflect current state
    const currentProgress = { current: startIndex, total: finalOutline.length }
    setProgress(currentProgress)
    
    // Create AbortController for this generation session
    const controller = new AbortController()
    generationController.current = controller
    
    saveState(AGENT_STATES.GENERATING, finalOutline, newGeneratedContent, currentProgress, true)
    
    if (isNewGeneration) {
      console.log(`🚀 Starting NEW generation from section 1/${finalOutline.length}`)
    } else {
      console.log(`🔄 Continuing existing generation from section ${startIndex + 1}/${finalOutline.length} (no credit consumption)`)
    }

    try {
      // Consume credits at the start (only if starting fresh)
      if (isNewGeneration) {
        await consumeCredits(AGENT_TYPES.NOVEL, 'Novel Agent')
      }
      
      for (let i = startIndex; i < finalOutline.length; i++) {
        // Check if generation should be paused or aborted
        if (controller.signal.aborted) {
          console.log('🔄 Generation aborted by controller')
          setIsLoading(false)
          return
        }
        

        
        const section = finalOutline[i]
        
        try {
          console.log(`📝 Generating section ${i + 1}/${finalOutline.length}: ${section.title}`)
          
          // Set current section being generated
          setCurrentSection(section.id)
          
          const sectionContent = await generateSectionContent(section, finalOutline, novelConfig, llmService, controller.signal)
          
          // Check again after async operation
          if (controller.signal.aborted) {
            console.log('🔄 Generation aborted during section generation')
            setCurrentSection(null)
            setIsLoading(false)
            return
          }
          

          
          newGeneratedContent[section.id] = sectionContent
          setGeneratedContent({ ...newGeneratedContent })
          
          const newProgress = { current: i + 1, total: finalOutline.length }
          setProgress(newProgress)
          saveState(AGENT_STATES.GENERATING, finalOutline, newGeneratedContent, newProgress, true)
          
          console.log(`✅ Section ${i + 1} completed`)
          
          // Clear current section when done
          setCurrentSection(null)
          
          // Add delay between sections to prevent rate limiting
          if (i < finalOutline.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        } catch (sectionError) {
          if (sectionError.name === 'AbortError') {
            console.log('🔄 Section generation aborted')
            setCurrentSection(null)
            setIsLoading(false)
            return
          }
          console.error(`❌ Error generating section ${i + 1}:`, sectionError)
          // Continue with next section instead of stopping completely
          newGeneratedContent[section.id] = `Error generating this section: ${sectionError.message}`
          setCurrentSection(null)
          
          // Update progress even for failed sections
          const newProgress = { current: i + 1, total: finalOutline.length }
          setProgress(newProgress)
          saveState(AGENT_STATES.GENERATING, finalOutline, newGeneratedContent, newProgress, true)
        }
      }
      
      setCurrentState(AGENT_STATES.COMPLETED)
      setIsLoading(false)
      saveState(AGENT_STATES.COMPLETED, finalOutline, newGeneratedContent, { current: finalOutline.length, total: finalOutline.length }, false)
      
      // Save to agent history (only if user is authenticated)
      try {
        if (user) {
          await AgentHistoryIntegration.saveNovelResult({
            query,
            outline: finalOutline,
            generatedContent: newGeneratedContent,
            progress: { current: finalOutline.length, total: finalOutline.length },
            novelConfig
          })
          console.log('✅ Novel result saved to history successfully')
        } else {
          console.log('⚠️ User not authenticated, skipping history save')
        }
      } catch (historyError) {
        console.error('Error saving to agent history:', historyError)
      }
      
      console.log('🎉 Novel generation completed successfully')
    } catch (error) {
      console.error('❌ Error during novel generation:', error)
      setError(error.message || 'Terjadi kesalahan saat menghasilkan novel')
      setIsLoading(false)
      // Save error state
      saveState(currentState, finalOutline, newGeneratedContent, progress, false)
    } finally {
      hasEverGenerated.current = true
    }
  }

  // Generate content for a single section with word count validation
  const generateSectionContent = async (section, fullOutline, novelConfig, llmService, abortSignal = null) => {
    const currentSectionIndex = fullOutline.indexOf(section)
    const contextSections = fullOutline.slice(0, currentSectionIndex)
    
    // Buat konteks lengkap dari SEMUA bagian sebelumnya untuk kontinuitas cerita
    let previousContext = ''
    if (contextSections.length > 0) {
      const allPreviousContent = contextSections
        .map(s => {
          const content = generatedContent[s.id]
          return content ? `## ${s.title}\n${content}` : ''
        })
        .filter(Boolean)
        .join('\n\n')
      
      // Gunakan semua konten sebelumnya, tapi batasi untuk menghindari token limit
      previousContext = allPreviousContent.length > 8000 ? 
        allPreviousContent.slice(-8000) : allPreviousContent
    }
    
    // Hitung target kata yang lebih akurat untuk mencapai 250-400 halaman
    const targetMinWords = section.minWords || 8000
    const targetMaxWords = section.maxWords || 15000
    
    // Function to validate word count
    const validateWordCount = (content) => {
      const wordCount = countWords(content)
      return {
        wordCount,
        isValid: wordCount >= targetMinWords && wordCount <= targetMaxWords,
        isTooShort: wordCount < targetMinWords,
        isTooLong: wordCount > targetMaxWords
      }
    }
    
    const prompt = `Anda adalah penulis novel profesional yang menggunakan struktur plot "${novelConfig.plotStructure}". Tulis ${section.title} untuk novel berjudul "${query}".

**DESKRIPSI BAGIAN:**
${section.description}

**KONFIGURASI NOVEL:**
- Struktur Plot: ${novelConfig.plotStructure}
- Genre: ${novelConfig.genre}
- Gaya Penulisan: ${novelConfig.writingStyle}
- Bahasa: ${novelConfig.language === 'id' ? 'Indonesia' : 'English'}
- **TARGET KATA WAJIB: ${targetMinWords.toLocaleString()}-${targetMaxWords.toLocaleString()} kata untuk bagian ini**
- Novel keseluruhan: 250-400 halaman (62,500-100,000 kata)

**PERSYARATAN JUMLAH KATA:**
- WAJIB mencapai minimal ${targetMinWords.toLocaleString()} kata
- Maksimal ${targetMaxWords.toLocaleString()} kata
- Jika kurang dari target minimum, tambahkan lebih banyak detail, dialog, dan deskripsi
- Jika melebihi maksimum, fokus pada elemen cerita yang paling penting

**PEDOMAN PENULISAN BERKUALITAS TINGGI:**
- Tulis dengan detail mendalam dan deskripsi yang vivid
- Kembangkan karakter dengan dialog yang autentik dan bermakna
- Gunakan teknik show don't tell
- Buat transisi yang mulus antar scene
- Tambahkan konflik internal dan eksternal
- Sertakan elemen sensori (visual, auditori, taktil, dll)
- Gunakan variasi panjang kalimat untuk ritme yang baik
- Pastikan setiap paragraf memajukan cerita
- Tambahkan foreshadowing dan callback ke bagian sebelumnya
- Buat ending bagian yang compelling untuk transisi ke bagian berikutnya
- **PENTING: Pastikan mencapai target jumlah kata yang ditentukan**

${previousContext ? `**KONTEKS LENGKAP DARI BAGIAN-BAGIAN SEBELUMNYA:**
${previousContext}

**PENTING:** Pastikan bagian ini melanjutkan cerita dengan mulus dari bagian sebelumnya. Jaga konsistensi karakter, plot, dan tone. Referensikan kejadian sebelumnya jika relevan.` : '**CATATAN:** Ini adalah bagian pembuka novel. Buat pembukaan yang kuat dan menarik.'}

**INSTRUKSI AKHIR:**
Tulis bagian ini dengan kualitas profesional dan detail yang mendalam. Pastikan mencapai target ${targetMinWords.toLocaleString()}-${targetMaxWords.toLocaleString()} kata. Jangan berhenti sampai target kata tercapai:`

    // Generate content with retry mechanism for word count validation
    let attempts = 0
    const maxAttempts = 3
    let finalContent = ''
    
    while (attempts < maxAttempts) {
      attempts++
      
      try {
        const currentPrompt = attempts === 1 ? prompt : 
          `${prompt}

**PERINGATAN KHUSUS - PERCOBAAN ${attempts}:**
Pada percobaan sebelumnya, jumlah kata tidak mencapai target. Kali ini, pastikan WAJIB menulis minimal ${targetMinWords.toLocaleString()} kata dan maksimal ${targetMaxWords.toLocaleString()} kata. Tambahkan lebih banyak detail, dialog, deskripsi scene, dan pengembangan karakter untuk mencapai target kata yang tepat.`
        
        const messages = [
          {
            role: 'user',
            content: currentPrompt
          }
        ]
        
        const response = await llmService.sendMessage(messages, {
          mode: MODEL_MODES.WRITE,
          additionalParams: {
            max_tokens: 100000, // Use full capacity of WRITE model
            temperature: 0.8
          },
          signal: abortSignal
        })
        
        // Validate word count
        const validation = validateWordCount(response)
        console.log(`📊 Bagian "${section.title}" - Percobaan ${attempts}: ${validation.wordCount} kata (Target: ${targetMinWords.toLocaleString()}-${targetMaxWords.toLocaleString()})`)
        
        if (validation.isValid) {
          console.log(`✅ Bagian "${section.title}" berhasil mencapai target kata: ${validation.wordCount} kata`)
          finalContent = response
          break
        } else if (validation.isTooShort && attempts < maxAttempts) {
          console.log(`⚠️ Bagian "${section.title}" terlalu pendek (${validation.wordCount} kata), mencoba lagi...`)
          continue
        } else if (validation.isTooLong) {
          console.log(`⚠️ Bagian "${section.title}" terlalu panjang (${validation.wordCount} kata), tetapi akan digunakan`)
          finalContent = response
          break
        } else {
          // Last attempt, use whatever we got
          console.log(`⚠️ Bagian "${section.title}" tidak mencapai target setelah ${maxAttempts} percobaan (${validation.wordCount} kata)`)
          finalContent = response
          break
        }
      } catch (error) {
        console.error(`❌ Error pada percobaan ${attempts} untuk bagian "${section.title}":`, error)
        if (attempts === maxAttempts) {
          throw new Error(`Gagal menghasilkan konten untuk ${section.title} setelah ${maxAttempts} percobaan`)
        }
      }
    }
    
    return finalContent
  }

  // Handler functions for NovelResultPage
  const handleContinueGeneration = async () => {
    if (!outline || !novelConfig) {
      console.error('Cannot continue: missing outline or config')
      return
    }
    
    const completedSections = Object.keys(generatedContent).length
    
    if (completedSections >= outline.length) {
      console.log('✅ All sections already completed')
      return
    }
    
    if (isLoading) {
      console.log('⚠️ Generation already in progress')
      return
    }
    
    console.log(`🔄 Continuing generation from section ${completedSections + 1}/${outline.length}`)
    await handleStartGeneration(outline, novelConfig)
  }

  const handleRegenerateSection = async (sectionId) => {
    if (!outline || !novelConfig) return
    
    const section = outline.find(s => s.id === sectionId)
    if (!section) return
    
    try {
      setCurrentSection(sectionId)
      const sectionContent = await generateSectionContent(section, outline, novelConfig, llmService)
      
      const updatedContent = { ...generatedContent, [sectionId]: sectionContent }
      setGeneratedContent(updatedContent)
      setCurrentSection(null)
      
      // Update saved state
      saveState(currentState, outline, updatedContent, progress)
    } catch (error) {
      console.error('Error regenerating section:', error)
      setError(`Gagal meregenerasi bagian: ${error.message}`)
      setCurrentSection(null)
    }
  }

  // Continue generation without credit consumption (for resume scenarios)
  const handleContinueGenerationWithoutCredits = async () => {
    if (!outline || !novelConfig) {
      console.error('Cannot continue: missing outline or config')
      return
    }
    
    // Filter out empty or null content to get actual completed sections
    const actualCompletedSections = Object.keys(generatedContent).filter(key => {
      const content = generatedContent[key]
      return content && content.trim().length > 0
    }).length
    
    console.log(`📊 Checking completion status: ${actualCompletedSections}/${outline.length} sections completed`)
    
    if (actualCompletedSections >= outline.length) {
      console.log('✅ All sections already completed')
      setCurrentState(AGENT_STATES.COMPLETED)
      setIsLoading(false)
      return
    }
    
    if (isLoading) {
      console.log('⚠️ Generation already in progress')
      return
    }
    
    console.log(`🔄 Continuing generation from section ${actualCompletedSections + 1}/${outline.length} (no credit consumption)`)
    
    setCurrentState(AGENT_STATES.GENERATING)
    setIsLoading(true)
    setError(null)

    
    // Update progress to reflect current state
    const currentProgress = { current: actualCompletedSections, total: outline.length }
    setProgress(currentProgress)
    
    // Create AbortController for this generation session
    const controller = new AbortController()
    generationController.current = controller
    
    saveState(AGENT_STATES.GENERATING, outline, generatedContent, currentProgress, true)
    
    try {
      // Continue generation loop from where it left off (NO CREDIT CONSUMPTION)
      const newGeneratedContent = { ...generatedContent }
      
      for (let i = actualCompletedSections; i < outline.length; i++) {
        // Check if generation should be aborted
        if (controller.signal.aborted) {
          console.log('🔄 Generation aborted by controller')
          setIsLoading(false)
          return
        }
        
        const section = outline[i]
        
        try {
          console.log(`📝 Generating section ${i + 1}/${outline.length}: ${section.title}`)
          
          // Set current section being generated
          setCurrentSection(section.id)
          
          const sectionContent = await generateSectionContent(section, outline, novelConfig, llmService, controller.signal)
          
          // Check again after async operation
          if (controller.signal.aborted) {
            console.log('🔄 Generation aborted during section generation')
            setCurrentSection(null)
            setIsLoading(false)
            return
          }
          
          // Add generated content
          newGeneratedContent[section.id] = sectionContent
          setGeneratedContent({ ...newGeneratedContent })
          
          // Update progress
          const newProgress = { current: i + 1, total: outline.length }
          setProgress(newProgress)
          
          // Save state after each section
          saveState(AGENT_STATES.GENERATING, outline, newGeneratedContent, newProgress, true)
          
          console.log(`✅ Section ${i + 1}/${outline.length} completed: ${section.title}`)
          
        } catch (sectionError) {
          console.error(`❌ Error generating section ${section.title}:`, sectionError)
          setError(`Gagal menghasilkan bagian "${section.title}": ${sectionError.message}`)
          setCurrentSection(null)
          setIsLoading(false)
          return
        }
      }
      
      // All sections completed
      setCurrentSection(null)
      setIsLoading(false)
      setCurrentState(AGENT_STATES.COMPLETED)
      
      saveState(AGENT_STATES.COMPLETED, outline, newGeneratedContent, { current: outline.length, total: outline.length }, false)
      
      // Save to agent history (only if user is authenticated)
      try {
        if (user) {
          await AgentHistoryIntegration.saveNovelResult({
            query,
            outline: outline,
            generatedContent: newGeneratedContent,
            progress: { current: outline.length, total: outline.length },
            novelConfig
          })
          console.log('✅ Novel result saved to history successfully')
        } else {
          console.log('⚠️ User not authenticated, skipping history save')
        }
      } catch (historyError) {
        console.error('Error saving to agent history:', historyError)
      }
      
      console.log('🎉 Novel generation completed successfully')
    } catch (error) {
      console.error('❌ Error during novel generation:', error)
      setError(error.message || 'Terjadi kesalahan saat menghasilkan novel')
      setIsLoading(false)
      saveState(currentState, outline, generatedContent, progress, false)
    }
  }

  // Handle back navigation
  const handleBack = () => {
    if (currentState === AGENT_STATES.GENERATING && isLoading) {
      // Don't allow back during generation
      return
    }
    
    if (currentState === AGENT_STATES.COMPLETED || currentState === AGENT_STATES.GENERATING) {
      // Reset to outline state and clear generated content to allow fresh start
      setCurrentState(AGENT_STATES.OUTLINE)
      setGeneratedContent({})
      setProgress({ current: 0, total: 0 })
      clearSessionStorage()
      console.log('🔄 Returning to outline - cleared generated content for fresh start')
    } else {
      clearSessionStorage()
      onBack()
    }
  }

  // Render current state
  const renderCurrentState = () => {
    switch (currentState) {
      case AGENT_STATES.OUTLINE:
        return (
          <NovelOutlinePage
            query={query}
            outline={outline}
            onOutlineChange={setOutline}
            onStartGeneration={handleStartGeneration}
            onGenerateOutline={handleGenerateOutline}
            onBack={handleBack}
            isLoading={isLoading}
            error={error}
            llmService={llmService}
          />
        )
      
      case AGENT_STATES.GENERATING:
      case AGENT_STATES.COMPLETED:
        return (
          <NovelResultPage
            query={query}
            outline={outline}
            novelConfig={novelConfig}
            generatedContent={generatedContent}
            currentSection={currentSection}
            isGenerating={currentState === AGENT_STATES.GENERATING}
            progress={progress}
            onBack={handleBack}
            onContinueGeneration={handleContinueGeneration}
             onRegenerateSection={handleRegenerateSection}
             error={error}
            startTime={startTime || Date.now()}
          />
        )
      
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      {renderCurrentState()}
      <CreditWarningModal 
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        message={warningModal.message}
      />
    </div>
  )
}

NovelAgentContent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired
}

const NovelAgent = (props) => {
  return (
    <AgentSubscriptionWrapper actionType="novel_agent" {...props}>
      <NovelAgentContent {...props} />
    </AgentSubscriptionWrapper>
  )
}

export default NovelAgent